import React from 'react';

import { Container, Row, Col, Card, Badge } from 'react-bootstrap';
import { 
    FaUsers, 
    FaGlobe, 
    FaStar, 
    FaAward, 
    FaRocket, 
    FaShieldAlt,
    FaHeart,
    FaLightbulb,
    FaHandshake,
    FaChartLine,
    FaLinkedin,
    FaTwitter,
    FaGithub,
    FaArrowRight,
    FaPlay,
    FaQuoteLeft,
    FaCheckCircle,
    FaTrophy
} from 'react-icons/fa';
import { motion, useScroll, useTransform } from 'framer-motion';
import './About.css';
import { useNavigate } from 'react-router-dom';

const About = () => {
    const { scrollYProgress } = useScroll();
    const y = useTransform(scrollYProgress, [0, 1], [0, -50]);
    const navigate = useNavigate();

    const stats = [
        { icon: FaUsers, number: "10,000+", label: "Happy Users", color: "#667eea" },
        { icon: FaGlobe, number: "500+", label: "Businesses Listed", color: "#764ba2" },
        { icon: FaStar, number: "4.8", label: "Average Rating", color: "#f59e0b" },
        { icon: FaAward, number: "50+", label: "Awards Won", color: "#10b981" }
    ];

    const teamMembers = [
        {
            name: "Sarah Johnson",
            role: "CEO & Founder",
            image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=300&fit=crop&crop=face",
            bio: "Visionary leader with 15+ years in tech industry",
            social: { linkedin: "#", twitter: "#", github: "#" },
            skills: ["Leadership", "Strategy", "Innovation"]
        },
        {
            name: "Michael Chen",
            role: "CTO",
            image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face",
            bio: "Tech expert passionate about scalable solutions",
            social: { linkedin: "#", twitter: "#", github: "#" },
            skills: ["Architecture", "Development", "AI/ML"]
        },
        {
            name: "Emily Rodriguez",
            role: "Head of Design",
            image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face",
            bio: "Creative designer focused on user experience",
            social: { linkedin: "#", twitter: "#", github: "#" },
            skills: ["UX/UI", "Branding", "Prototyping"]
        },
        {
            name: "David Kim",
            role: "Head of Marketing",
            image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face",
            bio: "Marketing strategist with growth mindset",
            social: { linkedin: "#", twitter: "#", github: "#" },
            skills: ["Growth", "Analytics", "Strategy"]
        }
    ];

    const timeline = [
        {
            year: "2020",
            title: "Company Founded",
            description: "Started with a vision to connect local businesses with communities",
            icon: FaRocket
        },
        {
            year: "2021",
            title: "First 100 Businesses",
            description: "Reached our first milestone with 100 verified business listings",
            icon: FaTrophy
        },
        {
            year: "2022",
            title: "Series A Funding",
            description: "Secured $5M in funding to scale our platform",
            icon: FaChartLine
        },
        {
            year: "2023",
            title: "10K+ Users Milestone",
            description: "Celebrated reaching 10,000 active users on our platform",
            icon: FaUsers
        },
        {
            year: "2024",
            title: "Global Expansion",
            description: "Expanding to serve businesses across multiple countries",
            icon: FaGlobe
        }
    ];

    const testimonials = [
        {
            name: "Jennifer Martinez",
            role: "Restaurant Owner",
            content: "Business Directory helped us reach 3x more customers in just 6 months. The platform is incredibly user-friendly and the support team is amazing!",
            rating: 5,
            image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face"
        },
        {
            name: "Robert Thompson",
            role: "Local Business Owner",
            content: "As a small business owner, finding customers was always a challenge. This platform changed everything for us. Highly recommended!",
            rating: 5,
            image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face"
        },
        {
            name: "Lisa Wang",
            role: "Marketing Director",
            content: "The analytics and insights provided by Business Directory have been invaluable for our marketing strategies. Great ROI!",
            rating: 5,
            image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face"
        }
    ];

    const values = [
        {
            icon: FaHeart,
            title: "Community First",
            description: "We believe in building strong local communities by connecting people with trusted businesses."
        },
        {
            icon: FaShieldAlt,
            title: "Trust & Transparency",
            description: "Every business on our platform is verified and reviewed to ensure quality and reliability."
        },
        {
            icon: FaRocket,
            title: "Innovation",
            description: "We continuously innovate to provide the best tools and features for businesses and users."
        },
        {
            icon: FaHandshake,
            title: "Partnership",
            description: "We work closely with businesses to help them grow and succeed in the digital age."
        }
    ];

    return (
        <div className="about-page">
            {/* Hero Section */}
            <section className="hero-section">
                <div className="hero-background">
                    <motion.div 
                        className="floating-shapes"
                        style={{ y }}
                    >
                        <div className="shape shape-1"></div>
                        <div className="shape shape-2"></div>
                        <div className="shape shape-3"></div>
                    </motion.div>
                </div>
                
                <Container>
                    <Row className="align-items-center min-vh-100">
                        <Col lg={6}>
                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <Badge bg="primary" className="hero-badge">
                                    <FaLightbulb /> About Us
                                </Badge>
                                <h1 className="hero-title">
                                    Connecting Communities with 
                                    <span className="highlight"> Trusted Businesses</span>
                                </h1>
                                <p className="hero-description">
                                    We're on a mission to revolutionize how people discover and connect with local businesses. 
                                    Our platform bridges the gap between communities and the services they need, creating 
                                    meaningful connections that drive growth and success.
                                </p>
                                <div className="hero-buttons">
                                    <motion.button 
                                        className="btn btn-primary btn-lg"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => navigate('/ourstory')}
                                    >
                                        Our Story <FaArrowRight />
                                    </motion.button>
                                    <motion.button 
                                        className="btn btn-outline-light btn-lg"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <FaPlay /> Watch Video
                                    </motion.button>
                                </div>
                            </motion.div>
                        </Col>
                        <Col lg={6}>
                            <motion.div
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                className="hero-image-container"
                            >
                                <div className="hero-image">
                                    <img 
                                        src="https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=600&h=400&fit=crop" 
                                        alt="Team collaboration"
                                        className="img-fluid rounded-4"
                                    />
                                </div>
                            </motion.div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Statistics Section */}
            <section className="stats-section">
                <Container>
                    <Row>
                        {stats.map((stat, index) => (
                            <Col lg={3} md={6} key={index}>
                                <motion.div
                                    className="stat-card"
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                >
                                    <div className="stat-icon" style={{ color: stat.color }}>
                                        <stat.icon />
                                    </div>
                                    <h3 className="stat-number">{stat.number}</h3>
                                    <p className="stat-label">{stat.label}</p>
                                </motion.div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* Story & Mission Section */}
            <section className="story-section">
                <Container>
                    <Row className="align-items-center">
                        <Col lg={6}>
                            <motion.div
                                initial={{ opacity: 0, x: -30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <h2 className="section-title">Our Story</h2>
                                <p className="section-description">
                                    Founded in 2020, Business Directory began as a simple idea: to make it easier for people 
                                    to find and connect with local businesses they can trust. What started as a small startup 
                                    has grown into a comprehensive platform serving thousands of businesses and users.
                                </p>
                                <p className="section-description">
                                    Our journey has been driven by a deep understanding of the challenges both businesses 
                                    and consumers face in the digital age. We've built our platform with these insights, 
                                    creating solutions that truly make a difference.
                                </p>
                                <div className="story-features">
                                    <div className="feature-item">
                                        <FaCheckCircle className="feature-icon" />
                                        <span>Verified Business Listings</span>
                                    </div>
                                    <div className="feature-item">
                                        <FaCheckCircle className="feature-icon" />
                                        <span>Advanced Search & Filters</span>
                                    </div>
                                    <div className="feature-item">
                                        <FaCheckCircle className="feature-icon" />
                                        <span>Real-time Reviews & Ratings</span>
                                    </div>
                                </div>
                            </motion.div>
                        </Col>
                        <Col lg={6}>
                            <motion.div
                                initial={{ opacity: 0, x: 30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                            >
                                <div className="mission-vision-cards">
                                    <Card className="mission-card">
                                        <Card.Body>
                                            <div className="card-icon">
                                                <FaRocket />
                                            </div>
                                            <h4>Our Mission</h4>
                                            <p>To empower local businesses and communities by creating meaningful connections 
                                               that drive growth, trust, and success for everyone involved.</p>
                                        </Card.Body>
                                    </Card>
                                    <Card className="vision-card">
                                        <Card.Body>
                                            <div className="card-icon">
                                                <FaGlobe />
                                            </div>
                                            <h4>Our Vision</h4>
                                            <p>To become the world's most trusted platform for discovering and connecting 
                                               with local businesses, fostering stronger communities worldwide.</p>
                                        </Card.Body>
                                    </Card>
                                </div>
                            </motion.div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Values Section */}
            <section className="values-section">
                <Container>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-5"
                    >
                        <h2 className="section-title">Our Core Values</h2>
                        <p className="section-description">
                            These principles guide everything we do and shape our culture
                        </p>
                    </motion.div>
                    
                    <Row>
                        {values.map((value, index) => (
                            <Col lg={3} md={6} key={index}>
                                <motion.div
                                    className="value-card"
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    whileHover={{ y: -10 }}
                                >
                                    <div className="value-icon">
                                        <value.icon />
                                    </div>
                                    <h4>{value.title}</h4>
                                    <p>{value.description}</p>
                                </motion.div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* Timeline Section */}
            <section className="timeline-section">
                <Container>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-5"
                    >
                        <h2 className="section-title">Our Journey</h2>
                        <p className="section-description">
                            Key milestones in our growth and development
                        </p>
                    </motion.div>
                    
                    <div className="timeline">
                        {timeline.map((item, index) => (
                            <motion.div
                                key={index}
                                className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}
                                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.2 }}
                            >
                                <div className="timeline-content">
                                    <div className="timeline-icon">
                                        <item.icon />
                                    </div>
                                    <div className="timeline-year">{item.year}</div>
                                    <h4>{item.title}</h4>
                                    <p>{item.description}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </Container>
            </section>

            {/* Team Section */}
            <section className="team-section">
                <Container>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-5"
                    >
                        <h2 className="section-title">Meet Our Team</h2>
                        <p className="section-description">
                            The passionate individuals behind our success
                        </p>
                    </motion.div>
                    
                    <Row>
                        {teamMembers.map((member, index) => (
                            <Col lg={3} md={6} key={index}>
                                <motion.div
                                    className="team-card"
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    whileHover={{ y: -10 }}
                                >
                                    <div className="team-image">
                                        <img src={member.image} alt={member.name} />
                                        <div className="team-social">
                                            <a href={member.social.linkedin}><FaLinkedin /></a>
                                            <a href={member.social.twitter}><FaTwitter /></a>
                                            <a href={member.social.github}><FaGithub /></a>
                                        </div>
                                    </div>
                                    <div className="team-info">
                                        <h4>{member.name}</h4>
                                        <p className="team-role">{member.role}</p>
                                        <p className="team-bio">{member.bio}</p>
                                        <div className="team-skills">
                                            {member.skills.map((skill, skillIndex) => (
                                                <Badge key={skillIndex} bg="light" text="dark">
                                                    {skill}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                </motion.div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* Testimonials Section */}
            <section className="testimonials-section">
                <Container>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-5"
                    >
                        <h2 className="section-title">What Our Users Say</h2>
                        <p className="section-description">
                            Real feedback from businesses and customers
                        </p>
                    </motion.div>
                    
                    <Row>
                        {testimonials.map((testimonial, index) => (
                            <Col lg={4} md={6} key={index}>
                                <motion.div
                                    className="testimonial-card"
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                >
                                    <div className="testimonial-content">
                                        <FaQuoteLeft className="quote-icon" />
                                        <p>{testimonial.content}</p>
                                    </div>
                                    <div className="testimonial-author">
                                        <img src={testimonial.image} alt={testimonial.name} />
                                        <div>
                                            <h5>{testimonial.name}</h5>
                                            <p>{testimonial.role}</p>
                                            <div className="rating">
                                                {[...Array(testimonial.rating)].map((_, i) => (
                                                    <FaStar key={i} className="star" />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* CTA Section */}


          



            <section className="cta-section">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8} className="text-center">
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8 }}
                            >
                                <h2>Ready to Join Our Community?</h2>
                                <p>Whether you're a business looking to grow or a customer seeking trusted services, 
                                   we're here to help you connect and succeed.</p>
                                <div className="cta-buttons">
                                    <motion.button 
                                        className="btn btn-primary btn-lg"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        List Your Business
                                    </motion.button>
                                    <motion.button 
                                        className="btn btn-outline-light btn-lg"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        Browse Businesses
                                    </motion.button>
                                </div>
                            </motion.div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default About; 